import { createError, ValidationError, ERROR_CODES } from './errors';
import { errorMessageUtils } from './errorMessages';

// Validation rule interface
export interface ValidationRule {
  test: (value: any) => boolean;
  message: string;
  code: string;
}

// Validation result interface
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  fieldErrors: Record<string, ValidationError[]>;
}

// Validation schema interface
export interface ValidationSchema {
  [field: string]: ValidationRule[];
}

// Common validation rules
export const validationRules = {
  // Required field validation
  required: (message?: string): ValidationRule => ({
    test: (value: any) => value !== null && value !== undefined && value !== '',
    message: message || 'This field is required',
    code: ERROR_CODES.REQUIRED_FIELD
  }),

  // Email validation
  email: (message?: string): ValidationRule => ({
    test: (value: any) => {
      if (!value) return true; // Skip if empty (use required rule for that)
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(String(value));
    },
    message: message || 'Please enter a valid email address',
    code: ERROR_CODES.INVALID_EMAIL
  }),

  // Phone number validation
  phone: (message?: string): ValidationRule => ({
    test: (value: any) => {
      if (!value) return true;
      const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
      return phoneRegex.test(String(value).replace(/[\s\-\(\)]/g, ''));
    },
    message: message || 'Please enter a valid phone number',
    code: ERROR_CODES.INVALID_PHONE
  }),

  // Date validation
  date: (message?: string): ValidationRule => ({
    test: (value: any) => {
      if (!value) return true;
      const date = new Date(value);
      return !isNaN(date.getTime());
    },
    message: message || 'Please enter a valid date',
    code: ERROR_CODES.INVALID_DATE
  }),

  // Minimum length validation
  minLength: (min: number, message?: string): ValidationRule => ({
    test: (value: any) => {
      if (!value) return true;
      return String(value).length >= min;
    },
    message: message || `Minimum length is ${min} characters`,
    code: ERROR_CODES.FIELD_TOO_SHORT
  }),

  // Maximum length validation
  maxLength: (max: number, message?: string): ValidationRule => ({
    test: (value: any) => {
      if (!value) return true;
      return String(value).length <= max;
    },
    message: message || `Maximum length is ${max} characters`,
    code: ERROR_CODES.FIELD_TOO_LONG
  }),

  // Numeric validation
  numeric: (message?: string): ValidationRule => ({
    test: (value: any) => {
      if (!value) return true;
      return !isNaN(Number(value));
    },
    message: message || 'Please enter a valid number',
    code: ERROR_CODES.INVALID_FORMAT
  }),

  slugForDomain: (message?: string): ValidationRule => ({
    test: (value: any) => {
      if (typeof value !== "string") return false; // must be a string
      const trimmed = value.trim();
      if (trimmed !== value) return false; // no leading/trailing spaces

      // Only lowercase letters, numbers, and hyphens — cannot start/end with hyphen
      const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

      return slugRegex.test(trimmed);
    },
    message:
      message ||
      "Please enter a valid slug — only lowercase letters, numbers, and hyphens (no spaces or uppercase letters)",
    code: ERROR_CODES.INVALID_FORMAT,
  }),


  // URL validation
  url: (message?: string): ValidationRule => ({
    test: (value: any) => {
      if (!value) return true;
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    },
    message: message || 'Please enter a valid URL',
    code: ERROR_CODES.INVALID_FORMAT
  }),

  // Pattern validation
  pattern: (regex: RegExp, message?: string): ValidationRule => ({
    test: (value: any) => {
      if (!value) return true;
      return regex.test(String(value));
    },
    message: message || 'Please enter a valid value',
    code: ERROR_CODES.INVALID_FORMAT
  }),

  // Custom validation
  custom: (test: (value: any) => boolean, message: string, code: string = ERROR_CODES.INVALID_FORMAT): ValidationRule => ({
    test,
    message,
    code
  }),

  // Choice validation (for select/radio/checkbox)
  choice: (choices: any[], message?: string): ValidationRule => ({
    test: (value: any) => {
      if (!value) return true;
      return choices.includes(value);
    },
    message: message || 'Please select a valid option',
    code: ERROR_CODES.INVALID_CHOICE
  }),

  // Range validation
  range: (min: number, max: number, message?: string): ValidationRule => ({
    test: (value: any) => {
      if (!value) return true;
      const num = Number(value);
      return !isNaN(num) && num >= min && num <= max;
    },
    message: message || `Value must be between ${min} and ${max}`,
    code: ERROR_CODES.INVALID_FORMAT
  }),

  // Password strength validation
  passwordStrength: (minLength: number = 8, message?: string): ValidationRule => ({
    test: (value: any) => {
      if (!value) return true;
      const password = String(value);
      const hasUpperCase = /[A-Z]/.test(password);
      const hasLowerCase = /[a-z]/.test(password);
      const hasNumbers = /\d/.test(password);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
      
      return password.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar;
    },
    message: message || `Password must be at least ${minLength} characters with uppercase, lowercase, number, and special character`,
    code: ERROR_CODES.INVALID_FORMAT
  })
};

// Validation utility class
export class Validator {
  private schema: ValidationSchema;
  private language: string;

  constructor(schema: ValidationSchema, language: string = 'en') {
    this.schema = schema;
    this.language = language;
  }

  // Validate a single field
  validateField(field: string, value: any): ValidationError[] {
    const rules = this.schema[field];
    if (!rules) return [];

    const errors: ValidationError[] = [];

    for (const rule of rules) {
      if (!rule.test(value)) {
        const error = createError.validation(rule.message, field, rule.code);
        errors.push(error);
      }
    }

    return errors;
  }

  // Validate all fields
  validate(data: Record<string, any>): ValidationResult {
    const errors: ValidationError[] = [];
    const fieldErrors: Record<string, ValidationError[]> = {};

    for (const [field] of Object.entries(this.schema)) {
      const value = data[field];
      const fieldValidationErrors = this.validateField(field, value);
      
      if (fieldValidationErrors.length > 0) {
        fieldErrors[field] = fieldValidationErrors;
        errors.push(...fieldValidationErrors);
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      fieldErrors
    };
  }

  // Validate specific fields
  validateFields(fields: string[], data: Record<string, any>): ValidationResult {
    const errors: ValidationError[] = [];
    const fieldErrors: Record<string, ValidationError[]> = {};

    for (const field of fields) {
      if (this.schema[field]) {
        const value = data[field];
        const fieldValidationErrors = this.validateField(field, value);
        
        if (fieldValidationErrors.length > 0) {
          fieldErrors[field] = fieldValidationErrors;
          errors.push(...fieldValidationErrors);
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      fieldErrors
    };
  }

  // Set language for validation messages
  setLanguage(language: string): void {
    this.language = language;
  }

  // Get localized validation message
  getLocalizedMessage(code: string, field?: string): string {
    const message = errorMessageUtils.getMessage(code, this.language);
    return errorMessageUtils.formatMessage(message, { field });
  }

  // Add validation rule to a field
  addRule(field: string, rule: ValidationRule): void {
    if (!this.schema[field]) {
      this.schema[field] = [];
    }
    this.schema[field].push(rule);
  }

  // Remove validation rule from a field
  removeRule(field: string, ruleIndex: number): void {
    if (this.schema[field] && this.schema[field][ruleIndex]) {
      this.schema[field].splice(ruleIndex, 1);
    }
  }

  // Get validation rules for a field
  getRules(field: string): ValidationRule[] {
    return this.schema[field] || [];
  }

  // Check if a field has validation rules
  hasRules(field: string): boolean {
    return !!(this.schema[field] && this.schema[field].length > 0);
  }
}

// Validation utility functions
export const validationUtils = {
  // Create a validator instance
  createValidator: (schema: ValidationSchema, language: string = 'en'): Validator => {
    return new Validator(schema, language);
  },

  // Quick validation functions
  isValidEmail: (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  isValidPhone: (phone: string): boolean => {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
  },

  isValidUrl: (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  },

  isValidDate: (date: string | Date): boolean => {
    const dateObj = new Date(date);
    return !isNaN(dateObj.getTime());
  },

  isNumeric: (value: any): boolean => {
    return !isNaN(Number(value));
  },

  isRequired: (value: any): boolean => {
    return value !== null && value !== undefined && value !== '';
  },

  // Format validation error for display
  formatValidationError: (error: ValidationError, language: string = 'en'): string => {
    if (error.field) {
      return `${error.field}: ${error.message}`;
    }
    return error.message;
  },

  // Get all validation errors as a single message
  getValidationMessage: (errors: ValidationError[], language: string = 'en'): string => {
    if (errors.length === 0) return '';
    if (errors.length === 1 && errors[0]) return validationUtils.formatValidationError(errors[0], language);
    
    const messages = errors.map(error => validationUtils.formatValidationError(error, language));
    return messages.join(', ');
  },

  // Debounce validation
  debounceValidation: <T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): ((...args: Parameters<T>) => void) => {
    let timeout: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  },

  // Async validation support
  asyncValidation: async <T>(
    value: T,
    validator: (value: T) => Promise<boolean>,
    message: string
  ): Promise<ValidationError | null> => {
    try {
      const isValid = await validator(value);
      return isValid ? null : createError.validation(message);
    } catch (error) {
      return createError.validation('Validation failed', undefined, error instanceof Error ? error.message : 'Unknown error');
    }
  }
};

// Predefined validation schemas
export const commonSchemas = {
  // User registration schema
  // userRegistration: {
  //   email: [validationRules.required(), validationRules.email()],
  //   password: [
  //     validationRules.required(),
  //     validationRules.minLength(8),
  //     validationRules.passwordStrength()
  //   ],
  //   confirmPassword: [validationRules.required()],
  //   firstName: [validationRules.required(), validationRules.minLength(2)],
  //   lastName: [validationRules.required(), validationRules.minLength(2)],
  //   phone: [validationRules.phone()]
  // },

  userRegistration: {
    // Required
    firstName: [validationRules.required(), validationRules.minLength(2)],
    organizationName: [validationRules.required(), validationRules.minLength(2)],
    email: [validationRules.required(), validationRules.email()],
  
    // Optional
    lastName: [],
    phone: [validationRules.phone()], // should pass if empty; validates format if provided
    pinCode: [],
    state: [],
    tenantSlugForDomain:[validationRules.required(),validationRules.slugForDomain()],
    // acceptTerms is handled in the component logic, so not validating here
  },
  

  // User login schema
  userLogin: {
    email: [validationRules.required(), validationRules.email()],
    password: [
      validationRules.required(),
      validationRules.minLength(8, 'Password must be at least 8 characters long')
    ]
  },
  
  // Password change schema
  passwordChange: {
    currentPassword: [validationRules.required()],
    newPassword: [
      validationRules.required(),
      validationRules.minLength(8),
      validationRules.passwordStrength()
    ],
    confirmPassword: [validationRules.required()]
  },

  // Password reset schema
  passwordReset: {
    password: [
      validationRules.required(),
      validationRules.minLength(8),
      validationRules.passwordStrength()
    ],
    confirmPassword: [validationRules.required()]
  },

  // Forgot password schema
  forgotPassword: {
    email: [validationRules.required(), validationRules.email()]
  },

  // Contact form schema
  contactForm: {
    name: [validationRules.required(), validationRules.minLength(2)],
    email: [validationRules.required(), validationRules.email()],
    message: [validationRules.required(), validationRules.minLength(10)]
  },

  // Profile update schema
  profileUpdate: {
    firstName: [validationRules.required(), validationRules.minLength(2)],
    lastName: [validationRules.required(), validationRules.minLength(2)],
    email: [validationRules.required(), validationRules.email()],
    phone: [validationRules.phone()],
    bio: [validationRules.maxLength(500)]
  }
}; 